import { useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { DocumentForm } from "@/components/DocumentForm";
import { SignaturePad } from "@/components/SignaturePad";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, CheckCircle2, FileText, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/prydes-logo.png";
import BrandLogo from "@/components/BrandLogo";
import { sanitizeDocHtml } from "@/components/RichTextEditor";

const CRIMSON = "#bf3b4f";

// Defined at module scope (NOT inside the component) so its identity is stable
// across re-renders — otherwise React remounts the whole page on every keystroke,
// blurring inputs and wiping the signature canvas.
const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex flex-col items-center py-8 px-4">
    <BrandLogo fallback={logo} className="h-10 mb-6" />
    <div className="w-full max-w-2xl">{children}</div>
    <p className="text-[11px] text-slate-400 mt-8 text-center max-w-md">If unexpected, please contact us at 1300 240 844 or info@prydessupport.com</p>
  </div>
);

export default function SignDocument() {
  const { token } = useParams();
  const [screen, setScreen] = useState("verify"); // verify|review|sign|success|signed|expired|invalid
  const [ndis, setNdis] = useState("");
  const [dob, setDob] = useState("");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sig, setSig] = useState("");
  const [typed, setTyped] = useState("");
  const [agree, setAgree] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    if (!ndis.trim() || !dob) { toast.error("Enter your NDIS number and date of birth."); return; }
    setBusy(true);
    try {
      const d = await api.publicSignVerify(token, { ndis_number: ndis, dob });
      setDoc(d); setScreen(d.already_signed ? "signed" : "review");
    } catch (err) {
      const s = err.response?.status;
      if (s === 410) setScreen("expired");
      else if (s === 404 || s === 409) setScreen("invalid");
      else toast.error("Those details didn't match. Please check your NDIS number and date of birth.");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!sig) { toast.error("Please draw your signature."); return; }
    if (!typed.trim()) { toast.error("Please type your full name."); return; }
    if (!agree) { toast.error("Please tick the confirmation box."); return; }
    setBusy(true);
    try {
      await api.publicSignSubmit(token, { ndis_number: ndis, dob, signature: sig, signature_text: typed.trim(), consent_acknowledged: agree });
      setScreen("success");
    } catch (err) {
      const s = err.response?.status;
      if (s === 409) setScreen(err.response?.data?.detail === "already_signed" ? "signed" : "invalid");
      else if (s === 410) setScreen("expired");
      else toast.error(err.response?.data?.detail || "Couldn't submit your signature. Please try again.");
    }
    finally { setBusy(false); }
  };

  if (screen === "expired") return <Shell><Card icon={Clock} colour="#f59e0b" title="This link has expired" text="For your security this signing link is no longer valid. Please contact our office and we'll send a fresh one." /></Shell>;
  if (screen === "invalid") return <Shell><Card icon={XCircle} colour="#dc2626" title="Link not available" text="This document isn't available for signing (it may already be signed or the link is invalid). Please contact our office." /></Shell>;
  if (screen === "signed") return <Shell><Card icon={CheckCircle2} colour="#00c86e" title="Already signed" text="This document has already been signed, so it can't be signed again. If something needs to change, please contact our office." /></Shell>;
  if (screen === "success") return <Shell><Card icon={CheckCircle2} colour="#00c86e" title="Thank you — you're all done" text="Your signature has been recorded. Our office will complete the document. You can close this page." /></Shell>;

  return (
    <Shell>
      {screen === "verify" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sign-verify">
          <div className="flex items-center gap-2 mb-1"><ShieldCheck className="h-5 w-5" style={{ color: CRIMSON }} /><h1 className="text-lg font-semibold text-slate-800">Verify your identity</h1></div>
          <p className="text-sm text-slate-500 mb-4">To open your document for signing, please confirm your details.</p>
          <form onSubmit={verify} className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-slate-600">NDIS number</Label>
              <Input value={ndis} onChange={(e) => setNdis(e.target.value)} className="mt-1 h-11 text-sm" data-testid="verify-ndis" autoFocus />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Date of birth</Label>
              <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="mt-1 h-11 text-sm" data-testid="verify-dob" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-11 text-white font-semibold" style={{ backgroundColor: CRIMSON }} data-testid="verify-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </Button>
          </form>
        </div>
      )}

      {screen === "review" && doc && (
        <div data-testid="sign-review">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm mb-4">
            <div className="flex items-center gap-2"><FileText className="h-5 w-5" style={{ color: CRIMSON }} /><h1 className="text-lg font-semibold text-slate-800">{doc.template_title}</h1></div>
            <p className="text-sm text-slate-500 mt-1">{doc.participant_name}{doc.participant_dob ? ` · DOB ${doc.participant_dob}` : ""}</p>
            <p className="text-xs text-slate-400 mt-2">Please review the {doc.kind === "service_agreement" ? "agreement" : "document"} below carefully, then continue to sign.</p>
          </div>
          {doc.kind === "service_agreement" ? (
            <div className="space-y-4" data-testid="sa-sign-review">
              {doc.sa?.agreement_terms && <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(doc.sa.agreement_terms) }} />}
              {doc.sa?.sos_preview_data && <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm overflow-x-auto" dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(doc.sa.sos_preview_data) }} />}
              {doc.sa?.financial_summary_data && <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm overflow-x-auto" dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(doc.sa.financial_summary_data) }} />}
            </div>
          ) : (
            <DocumentForm schema={doc.schema} values={doc.values} onChange={() => {}} disabled />
          )}
          <Button className="w-full h-11 mt-4 text-white font-semibold" style={{ backgroundColor: CRIMSON }} onClick={() => setScreen("sign")} data-testid="review-continue">
            Continue to sign
          </Button>
        </div>
      )}

      {screen === "sign" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sign-capture">
          <h1 className="text-lg font-semibold text-slate-800 mb-1">Your signature</h1>
          <p className="text-sm text-slate-500 mb-4">Sign in the box, type your full name, and confirm.</p>
          <SignaturePad onChange={setSig} />
          <div className="mt-3">
            <Label className="text-xs font-semibold text-slate-600">Full name</Label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type your full name" className="mt-1 h-11 text-sm" data-testid="sign-typed" />
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-600 mt-3">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#bf3b4f]" data-testid="sign-agree" />
            I confirm I have read and understood this document, and that this is my electronic signature.
          </label>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="h-11 flex-1" onClick={() => setScreen("review")}>Back</Button>
            <Button className="h-11 flex-1 text-white font-semibold" style={{ backgroundColor: CRIMSON }} onClick={submit} disabled={busy} data-testid="sign-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit signature"}
            </Button>
          </div>
        </div>
      )}
    </Shell>
  );
}

function Card({ icon: Icon, colour, title, text }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm text-center">
      <Icon className="h-12 w-12 mx-auto mb-3" style={{ color: colour }} />
      <h1 className="text-lg font-semibold text-slate-800">{title}</h1>
      <p className="text-sm text-slate-500 mt-2">{text}</p>
    </div>
  );
}
